import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Snackbar,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import ArchiveIcon from "@mui/icons-material/Archive";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import {
  adminSurfaceSx,
  adminClickableCardSx,
  adminPageSx,
  adminChipSx,
  MONO,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import ChatTimelineDialog from "./ChatTimelineDialog";
import { adminRequest } from "../../utils/adminApi";

// Admin "Coach Chats" console: enter a user's email, list their coach
// conversations (id / title / last activity), and click one to open the full
// chat timeline (ChatTimelineDialog) with per-turn tools + copyable debug — the
// same dialog Feedback Triage uses, so any conversation is inspectable, not just
// flagged ones. Fires through the getUserConversations proxy → worker
// GET /api/admin/user-conversations?user=<email> (admin-gated server-side).
//
// env-aware: prod uses the authSlice token; staging uses the held staging token
// and routes via ?env=staging (adminRequest). Mirrors PurgeWorkouts' lookup UX
// and FeedbackTriage's list→timeline flow.

// last_message_at / created_at come off the DO as SQLite's CURRENT_TIMESTAMP
// ("YYYY-MM-DD HH:MM:SS", UTC, no zone marker). dayjs would read that as LOCAL
// time and shift it — normalise to ISO-UTC first so the displayed time is right.
const fmtTs = (raw) => {
  if (!raw) return "—";
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? raw.replace(" ", "T") + "Z"
    : raw;
  const d = dayjs(iso);
  return d.isValid() ? d.format("MMM D, YYYY HH:mm") : raw;
};

export default function CoachChats() {
  const theme = useTheme();
  const { env, token } = useSelector(adminRequest);
  const [email, setEmail] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversations, setConversations] = useState(null); // null = never searched
  const [searchedEmail, setSearchedEmail] = useState("");
  const [selected, setSelected] = useState(null); // { conversation_id, user_email }
  const [snack, setSnack] = useState("");

  const loadConversations = useCallback(async () => {
    const target = email.trim();
    if (!target) {
      setEmailError("User email is required");
      return;
    }
    setEmailError("");
    if (env === "staging" && !token) {
      setSnack("Log in to staging first.");
      return;
    }
    setLoading(true);
    try {
      // env is routing-only; the proxy strips it before forwarding.
      const params = new URLSearchParams({ user: target });
      if (includeArchived) params.set("includeArchived", "1");
      if (env === "staging") params.set("env", "staging");
      const response = await axios.get(
        `/api/admin/getUserConversations?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setConversations(response.data?.conversations ?? []);
      setSearchedEmail(target);
    } catch (err) {
      console.error("Failed to load user conversations:", err);
      const message =
        err.response?.data?.error ||
        err.message ||
        "Failed to load conversations.";
      setSnack(message);
      setConversations([]);
      setSearchedEmail(target);
    } finally {
      setLoading(false);
    }
  }, [email, includeArchived, env, token]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") loadConversations();
  };

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<ForumIcon />}
          iconAccent={theme.palette.primary.main}
          title="Coach chats"
          subtitle="Look up a user by email, browse every coach conversation, and open one to evaluate the coach's turns and tool calls."
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <TextField
                  fullWidth
                  label="User email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onKeyDown}
                  error={!!emailError}
                  helperText={emailError}
                  margin="normal"
                  slotProps={{ htmlInput: { "data-testid": "coach-chats-email" } }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeArchived}
                        onChange={(e) => setIncludeArchived(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include archived"
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <ForumIcon />
                      )
                    }
                    onClick={loadConversations}
                    disabled={loading}
                    data-testid="coach-chats-load"
                  >
                    Load chats
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {conversations !== null && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.text.secondary, mb: 1.5 }}
            >
              {conversations.length === 0
                ? `No conversations for ${searchedEmail}.`
                : `${conversations.length} conversation${
                    conversations.length === 1 ? "" : "s"
                  } for ${searchedEmail}`}
            </Typography>

            <Stack spacing={1.5}>
              {conversations.map((c) => (
                <Card
                  key={c.id}
                  sx={adminClickableCardSx(theme)}
                  onClick={() =>
                    setSelected({
                      conversation_id: c.id,
                      user_email: searchedEmail,
                    })
                  }
                  data-testid="coach-chats-row"
                >
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 0.25 }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                            noWrap
                          >
                            {c.title || "Untitled chat"}
                          </Typography>
                          {c.archived && (
                            <Chip
                              size="small"
                              icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
                              label="Archived"
                              sx={adminChipSx(theme, theme.palette.text.secondary)}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary, fontFamily: MONO }}
                          noWrap
                        >
                          {c.id}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {fmtTs(c.last_message_at || c.created_at)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <ChatTimelineDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        conversationId={selected?.conversation_id}
        userEmail={selected?.user_email}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
